import {
  apiToggleDeactivation,
  createInspector,
  getInspector,
  getInspectors,
  setAvailability,
  setEmploymentDetails,
  setPersonalDetails,
  setUnavailablePeriods,
} from 'app/api/dcra-inspections/inspectors';
import axios from 'axios';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppNS } from '../..';
import { AppThunk } from '../index';

export type InspectorsState = {
  currentInspector: AppNS.Inspector | null;
  isLoading: boolean;
  isUserAdding: boolean;
  isUserUpdating: boolean;
  isUserDeleting: boolean;
  inspectors: Array<AppNS.Inspector>;
  page: number;
  perPage: number;
  search: string;
  inspectorStatus: string;
  cluster: string;
  totalItems: number;
  userAddError: string | null;
  userUpdateError: string | null;
  userDeleteError: string | null;
};

const initialState: InspectorsState = {
  currentInspector: null,
  isLoading: false,
  isUserAdding: false,
  isUserUpdating: false,
  isUserDeleting: false,
  inspectors: [],
  page: 1,
  perPage: 10,
  search: '',
  inspectorStatus: '',
  cluster: '',
  totalItems: 0,
  userAddError: '',
  userUpdateError: '',
  userDeleteError: '',
};

function startLoading(state: InspectorsState) {
  state.isLoading = true;
}

const inspectorsSlice = createSlice({
  name: 'inspectors',
  initialState,
  reducers: {
    getInspectorsStart: startLoading,
    resetCollection(state) {
      state.inspectors = [];
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setCurrentParams(state, action: PayloadAction<AppNS.Params>) {
      state.page = action.payload.page;
      state.search = action.payload.search;
      state.inspectorStatus = action.payload.inspectorStatus;
      state.cluster = action.payload.cluster;
    },
    getInspectorsSuccess(
      state: InspectorsState,
      action: PayloadAction<AppNS.Params>
    ) {
      state.isLoading = false;
      state.inspectors = action.payload.results;
      state.totalItems = action.payload.totalEntries;
    },
    getInspectorsFailed(state: InspectorsState, action: PayloadAction<string>) {
      state.inspectors = [];
      state.currentInspector = null;
      state.isLoading = false;
    },
    getInspectorSuccess(
      state: InspectorsState,
      action: PayloadAction<AppNS.Params>
    ) {
      state.isLoading = false;
      state.currentInspector = action.payload as AppNS.Inspector;
    },
    addInspectorStart(state: InspectorsState) {
      state.userAddError = null;
      state.isUserAdding = true;
    },
    addInspectorFailed(state: InspectorsState, action: PayloadAction<string>) {
      state.userAddError = action.payload;
      state.isUserAdding = false;
    },
    addInspectorSuccess(
      state: InspectorsState,
      action: PayloadAction<AppNS.Params>
    ) {
      state.currentInspector = {
        id: action.payload.id,
        email: action.payload.email,
        ...action.payload.properties,
      };
      state.isUserAdding = false;
      state.userAddError = null;
    },
    updateInspectorStart(state) {
      state.isUserUpdating = true;
    },
    updateInspectorFailed(
      state: InspectorsState,
      action: PayloadAction<string>
    ) {
      state.userUpdateError = action.payload;
      state.isUserUpdating = false;
    },
    updateInspectorSuccess(
      state: InspectorsState,
      action: PayloadAction<AppNS.Params>
    ) {
      state.currentInspector = {
        ...state.currentInspector,
        ...action.payload.properties,
        email: action.payload.email,
      };
      state.isUserUpdating = false;
      state.userUpdateError = null;
    },
    deleteInspectorSuccess(
      state: InspectorsState,
      action: PayloadAction<string>
    ) {
      state.isUserDeleting = false;
      state.userDeleteError = null;
    },
    deleteUserFailed(state: InspectorsState, action: PayloadAction<string>) {
      state.userDeleteError = action.payload;
      state.isUserDeleting = false;
    },
    resetErrors(state: InspectorsState) {
      state.userUpdateError = null;
    },
  },
});

export const {
  addInspectorFailed,
  addInspectorSuccess,
  addInspectorStart,
  deleteUserFailed,
  deleteInspectorSuccess,
  getInspectorSuccess,
  getInspectorsFailed,
  getInspectorsStart,
  getInspectorsSuccess,
  resetCollection,
  setCurrentPage,
  setCurrentParams,
  updateInspectorSuccess,
  updateInspectorFailed,
  updateInspectorStart,
  resetErrors,
} = inspectorsSlice.actions;

export default inspectorsSlice.reducer;

export const fetchInspectors =
  (params: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getInspectorsStart());
      const usersResponse = await getInspectors(params);
      dispatch(getInspectorsSuccess(usersResponse));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      dispatch(getInspectorsFailed(err.toString()));
    }
  };

export const fetchInspector =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getInspectorsStart());
      const usersResponse = await getInspector(id);
      dispatch(getInspectorSuccess(usersResponse));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      dispatch(getInspectorsFailed(err.toString()));
    }
  };

export const addAdminUser =
  (params: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      const user = await createInspector(params);
      dispatch(addInspectorSuccess(user));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
    }
  };

export const setPersonalDetailsData =
  (requestData: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateInspectorStart());
      const response = await setPersonalDetails(requestData);
      dispatch(updateInspectorSuccess(response));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(updateInspectorFailed(err));
    }
  };

export const setActiveState =
  (action: 'deactivate' | 'activate', requestData: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateInspectorStart());
      const response = await apiToggleDeactivation(action, requestData);
      dispatch(updateInspectorSuccess(response));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(updateInspectorFailed(err));
    }
  };

export const setUnavailabilityData =
  (requestData: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateInspectorStart());
      const response = await setUnavailablePeriods(requestData);
      dispatch(updateInspectorSuccess(response));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(updateInspectorFailed(err));
    }
  };

export const setAvailabilityData =
  (requestData: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateInspectorStart());
      const response = await setAvailability(requestData);
      dispatch(updateInspectorSuccess(response));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(updateInspectorFailed(err));
    }
  };

export const setEmploymentData =
  (requestData: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateInspectorStart());
      const response = await setEmploymentDetails(requestData);
      dispatch(updateInspectorSuccess(response));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(updateInspectorFailed(err));
    }
  };
